import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Camera, ShoppingCart, X, Check, ArrowRight, Calendar, ArrowLeft, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { GalleryVideosTab } from '../components/gallery/GalleryVideosTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventGalleryPage = () => {
  const { eventId } = useParams();
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [eventPhotos, setEventPhotos] = useState([]);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [cart, setCart] = useState([]); // array of photo objects
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState(user?.email || '');
  const [checkoutName, setCheckoutName] = useState(user?.name || '');
  const [checkingOut, setCheckingOut] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Fetch pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API_URL}/api/media-pricing`, { headers });
        setPricing(res.data);
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, [user]);

  // Fetch photos
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (eventId) {
          const res = await axios.get(`${API_URL}/api/event-photos?event_id=${eventId}&limit=500`);
          const evs = res.data.events || [];
          if (evs.length > 0) {
            setEventInfo(evs[0]);
            setEventPhotos(evs[0].photos || []);
          } else {
            setEventInfo(null);
            setEventPhotos([]);
          }
        } else {
          const res = await axios.get(`${API_URL}/api/event-photos?per_event=8`);
          setEvents(res.data.events || []);
        }
      } catch (err) {
        console.error('Failed to load gallery:', err);
        toast.error('Failed to load photos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventId]);

  // Restore cart from sessionStorage
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('photo_cart');
      if (stored) setCart(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  // Persist cart
  useEffect(() => {
    sessionStorage.setItem('photo_cart', JSON.stringify(cart));
  }, [cart]);

  const inCart = useCallback((photoId) => cart.some(p => p.id === photoId), [cart]);

  const toggleCart = (photo) => {
    setCart(prev => {
      if (prev.some(p => p.id === photo.id)) {
        return prev.filter(p => p.id !== photo.id);
      }
      return [...prev, photo];
    });
  };

  const cartTotal = pricing ? (cart.length * pricing.photo_price).toFixed(2) : '0.00';
  const perPhoto = pricing?.photo_price?.toFixed(2) || '0.00';

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (!checkoutEmail || !checkoutEmail.includes('@')) {
      toast.error('Please provide a valid email');
      return;
    }
    setCheckingOut(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_URL}/api/photos/cart/checkout`, {
        photo_ids: cart.map(p => p.id),
        customer_email: checkoutEmail,
        customer_name: checkoutName,
        origin_url: window.location.origin,
      }, { headers });
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Checkout failed:', err);
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {eventId && (
            <Link to="/gallery" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-gallery">
              <ArrowLeft className="w-4 h-4 mr-1" /> All Events
            </Link>
          )}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight text-foreground">
                {eventInfo?.event_title || 'Event Photos'}
              </h1>
              {eventInfo?.event_date && (
                <p className="text-muted-foreground mt-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(eventInfo.event_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Edmonton' })}
                </p>
              )}
              {!eventId && (
                <p className="font-serif text-lg text-muted-foreground mt-2 max-w-2xl">
                  Browse photos from our jackpots and events. Watermarked previews. Purchase to download in full quality.
                </p>
              )}
            </div>
            {pricing && (
              <div className="bg-background border border-border rounded-xl px-4 py-3 text-right">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Photos &amp; Videos</p>
                <p className="font-display font-bold text-2xl text-primary">${perPhoto}</p>
                <p className="text-xs text-muted-foreground">
                  {pricing.is_subscriber ? 'Subscriber pricing (each)' : pricing.is_admin_or_coach ? 'Free for coaches/admins' : (
                    <>Subscribers pay ${pricing.pricing.photo_price_sub.toFixed(2)} each — <Link to="/signup" className="text-primary underline">Join</Link></>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="photos" className="w-full">
          <TabsList className="mb-6" data-testid="gallery-tabs">
            <TabsTrigger value="photos" className="flex items-center gap-2" data-testid="gallery-tab-photos">
              <Camera className="w-4 h-4" /> Photos
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2" data-testid="gallery-tab-videos">
              <Video className="w-4 h-4" /> Videos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos">
        {/* Single event view */}
        {eventId && eventPhotos.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            <Camera className="w-16 h-16 mx-auto mb-4 opacity-40" />
            <p>No photos uploaded for this event yet.</p>
          </div>
        )}

        {eventId && eventPhotos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {eventPhotos.map((photo) => {
              const selected = inCart(photo.id);
              return (
                <div
                  key={photo.id}
                  className={`relative aspect-square rounded-lg overflow-hidden group cursor-pointer transition-all ${selected ? 'ring-4 ring-primary' : 'ring-1 ring-border'}`}
                  data-testid={`gallery-photo-${photo.id}`}
                >
                  {/* Watermarked preview */}
                  <div
                    className="absolute inset-0 bg-muted"
                    onClick={() => setLightboxPhoto(photo)}
                  >
                    <img
                      src={`${API_URL}/api/event-photos/${photo.id}/thumb`}
                      alt={photo.filename}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    {/* CSS watermark overlay */}
                    <WatermarkOverlay />
                  </div>
                  {/* Cart toggle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleCart(photo); }}
                    className={`absolute top-2 right-2 z-30 rounded-full p-2 shadow-lg transition-colors ${selected ? 'bg-primary text-primary-foreground' : 'bg-white/90 text-foreground hover:bg-white'}`}
                    data-testid={`gallery-cart-toggle-${photo.id}`}
                    title={selected ? 'Remove from cart' : 'Add to cart'}
                  >
                    {selected ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* All events grid view */}
        {!eventId && (
          <div className="space-y-12">
            {events.length === 0 && (
              <div className="text-center py-20 text-muted-foreground">
                <Camera className="w-16 h-16 mx-auto mb-4 opacity-40" />
                <p>No event photos available yet.</p>
              </div>
            )}
            {events.map((ev) => (
              <div key={ev.event_id} data-testid={`gallery-event-${ev.event_id}`}>
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight">{ev.event_title}</h2>
                    {ev.event_date && (
                      <p className="text-sm text-muted-foreground">
                        {new Date(ev.event_date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Edmonton' })}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">{ev.total_photos} photo{ev.total_photos !== 1 ? 's' : ''}</p>
                  </div>
                  <Link to={`/gallery/${ev.event_id}`}>
                    <Button variant="outline" size="sm" data-testid={`view-event-${ev.event_id}`}>
                      View All <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                  {(ev.photos || []).slice(0, 8).map((photo) => {
                    const selected = inCart(photo.id);
                    return (
                      <div
                        key={photo.id}
                        className={`relative aspect-square rounded-md overflow-hidden group cursor-pointer ${selected ? 'ring-2 ring-primary' : ''}`}
                      >
                        <div
                          className="absolute inset-0 bg-muted"
                          onClick={() => setLightboxPhoto(photo)}
                        >
                          <img
                            src={`${API_URL}/api/event-photos/${photo.id}/thumb`}
                            alt={photo.filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                          />
                          <WatermarkOverlay small />
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCart(photo); }}
                          className={`absolute top-1 right-1 z-30 rounded-full p-1.5 shadow ${selected ? 'bg-primary text-primary-foreground' : 'bg-white/90 hover:bg-white'}`}
                          title={selected ? 'Remove from cart' : 'Add to cart'}
                        >
                          {selected ? <Check className="w-3 h-3" /> : <ShoppingCart className="w-3 h-3" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Video order CTA on single event view */}
        {eventId && eventInfo && (
          <div className="mt-12 bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-2xl p-6 md:p-8 text-center">
            <h3 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight">Need Custom Video Edits?</h3>
            <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
              We edit videos on-demand. Tell us the rider, run, and details — we&apos;ll find the footage and edit your video.
            </p>
            <Link to={`/events/${eventInfo.event_id}/order-video`}>
              <Button size="lg" className="mt-5 rounded-full px-8 font-bold uppercase tracking-wider" data-testid="order-video-btn">
                Order Video From This Event <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3 italic">Note: not every run is guaranteed to have been captured.</p>
          </div>
        )}
          </TabsContent>

          <TabsContent value="videos">
            <GalleryVideosTab pricing={pricing} filterEventId={eventId} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating cart button */}
      {cart.length > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-primary text-primary-foreground rounded-full px-6 py-4 shadow-2xl flex items-center gap-3 hover:scale-105 transition-transform"
          data-testid="open-cart-btn"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-bold">{cart.length} photo{cart.length !== 1 ? 's' : ''} · ${cartTotal}</span>
        </button>
      )}

      {/* Cart sidebar */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md bg-background h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl uppercase">Your Cart</h2>
              <button onClick={() => setCartOpen(false)} className="p-2 hover:bg-muted rounded-full" data-testid="close-cart-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {cart.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Your cart is empty</p>
              ) : (
                cart.map(photo => (
                  <div key={photo.id} className="flex items-center gap-3 p-2 border border-border rounded-lg">
                    <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img src={`${API_URL}/api/event-photos/${photo.id}/thumb`} alt="" className="w-full h-full object-cover" draggable={false} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{photo.event_title || 'Event Photo'}</p>
                      <p className="text-xs text-muted-foreground truncate">{photo.filename}</p>
                    </div>
                    <button onClick={() => toggleCart(photo)} className="text-muted-foreground hover:text-destructive p-1" title="Remove">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}

              {cart.length > 0 && pricing && (
                <>
                  <div className="border-t border-border pt-4 mt-4 space-y-3">
                    <div>
                      <Label htmlFor="checkout-name">Your Name</Label>
                      <Input
                        id="checkout-name"
                        value={checkoutName}
                        onChange={(e) => setCheckoutName(e.target.value)}
                        placeholder="Full name"
                        data-testid="checkout-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="checkout-email">Email *</Label>
                      <Input
                        id="checkout-email"
                        type="email"
                        value={checkoutEmail}
                        onChange={(e) => setCheckoutEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        data-testid="checkout-email-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Receipt sent here. Photos download immediately after payment.</p>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4 mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{cart.length} photo{cart.length !== 1 ? 's' : ''} × ${perPhoto}</span>
                      <span className="font-semibold">${cartTotal}</span>
                    </div>
                    {!pricing.is_subscriber && !pricing.is_admin_or_coach && (
                      <p className="text-xs text-primary bg-primary/10 rounded p-2">
                        💡 Subscribers pay just ${pricing.pricing.photo_price_sub.toFixed(2)}/photo. <Link to="/signup" className="underline font-bold">Join now</Link>
                      </p>
                    )}
                    <Button
                      onClick={handleCheckout}
                      disabled={checkingOut || cart.length === 0}
                      className="w-full font-bold uppercase tracking-wider"
                      size="lg"
                      data-testid="checkout-btn"
                    >
                      {checkingOut ? 'Redirecting...' : `Pay $${cartTotal}`}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)}>
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full z-10"
            onClick={() => setLightboxPhoto(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <div className="relative max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxPhoto.url}
              alt={lightboxPhoto.filename}
              className="max-w-full max-h-[80vh] object-contain"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            <WatermarkOverlay large />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 flex items-center justify-between flex-wrap gap-3">
              <p className="text-white font-medium">{lightboxPhoto.event_title}</p>
              <Button
                onClick={(e) => { e.stopPropagation(); toggleCart(lightboxPhoto); }}
                variant={inCart(lightboxPhoto.id) ? 'secondary' : 'default'}
                data-testid="lightbox-cart-btn"
              >
                {inCart(lightboxPhoto.id) ? (
                  <><Check className="w-4 h-4 mr-2" /> In Cart</>
                ) : (
                  <><ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart · ${perPhoto}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tiled CSS watermark overlay
const WatermarkOverlay = ({ small, large }) => (
  <div
    className="absolute inset-0 pointer-events-none select-none flex flex-wrap content-center justify-center overflow-hidden"
    style={{
      backgroundImage: `repeating-linear-gradient(-30deg, transparent, transparent 80px, rgba(0,0,0,0.001) 80px, rgba(0,0,0,0.001) 81px)`,
    }}
  >
    <div
      className={`w-full h-full flex items-center justify-center font-display font-black uppercase text-white/40 tracking-widest`}
      style={{
        fontSize: large ? '2.5rem' : small ? '0.7rem' : '1.2rem',
        transform: 'rotate(-30deg)',
        textShadow: '0 0 10px rgba(0,0,0,0.5)',
        lineHeight: '2.5',
        wordSpacing: '2rem',
      }}
    >
      {Array.from({ length: large ? 20 : small ? 10 : 14 }).map((_, i) => (
        <span key={i} className="px-3">ELEVATED ROPING MEDIA</span>
      ))}
    </div>
  </div>
);

export default EventGalleryPage;
